<template>
  <li class="video-row">
    <div class="container">
      <VideoRowThumbnail
        :url="computedThumbnailUrl"
        :isGeneratingThumbnail="isGeneratingThumbnail"
      />

      <div class="title-and-size">
        <p class="title">
          {{ this.titleMessage }}
        </p>
        <p class="status-message">
          <span>{{this.statusMessage}}</span>
          &nbsp;
        </p>
      </div>

      <span
        v-if="shouldShowRemoveButtonAndReorderHandle"
        class="icon delete"
        @click="onRemoveOrCancelClicked"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path
          d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/><path
          d="M0 0h24v24H0z" fill="none"/></svg>
      </span>

      <span
        v-if="shouldShowRemoveButtonAndReorderHandle"
        class="icon handle"
      >
        <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="24" height="24"
             viewBox="0 0 24 24">
          <defs><path id="a" d="M0 0h24v24H0V0z" /></defs>
          <clipPath id="b"><use xlink:href="#a" overflow="visible" /></clipPath>
          <path clip-path="url(#b)" d="M20 9H4v2h16V9zM4 15h16v-2H4v2z" />
        </svg>
      </span>
    </div>
    <ThumbnailGenerator
      ref="thumbnailGenerator"
      :file="file"
      @completed="onThumbnailGenerationCompleted"
      @error="thumbnailUnavailable"
    />
  </li>
</template>

<script>
import filesize from 'filesize';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import VideoUploaderUtil from './VideoUploaderUtil.js'
import {NetworkingError, RequestAbortedError} from '../../service/S3Constants.js';
import ThumbnailGenerator from './ThumbnailGenerator.vue'
import VideoRowThumbnail from "./VideoRowThumbnail";

momentDurationFormatSetup(moment);

export default {
  name: 'video-row',
  components: {
    VideoRowThumbnail,
    ThumbnailGenerator
  },
  props: {
    uuid: String,
    title: String,
    originalFilename: String,
    publicUrl: String,
    fileSize: Number,
    file: File,
    thumbnailUrl: String,
  },
  data() {
    return {
      managedUpload: null, // Reference to an upload instance of S3 SDK
      isUploadInProgress: false,
      startedUploadingAt: null,
      totalFileSize: 0,
      uploadedBytes: 0,
      progress: 0.0,
      isGeneratingThumbnail: false,
      newThumbnailUrl: "",
      isThumbnailLoaded: false,
      util: null,
    };
  },
  computed: {
    titleMessage() {
      if (this.title) {
        return this.title;
      } else if (this.originalFilename) {
        return this.originalFilename;
      } else {
        return 'Untitled';
      }
    },
    statusMessage() {
      // Case 1: Uploading a file
      if (this.isGeneratingThumbnail) {
        return 'Preparing to upload a video';
      }
      else if (this.isUploadInProgress) {
        const uploaded = filesize(this.uploadedBytes, {round: 0});
        const total = filesize(this.totalFileSize, {round: 0});

        const timeElapsed = ((new Date()).getTime() - this.startedUploadingAt.getTime());
        const timeRemaining = timeElapsed / this.uploadedBytes * (this.totalFileSize - this.uploadedBytes);
        const eta = moment.duration(timeRemaining);

        return `Uploading: ${uploaded} / ${total}, ETA: ${eta.format('h[h] m[m] s[s]', {trim: 'both'})}`;
      }
      // Case 2: Just render stored info
      else if (Number(this.fileSize) > 0) {
        return filesize(this.fileSize, {round: 0});
      }
    },
    computedThumbnailUrl() {
      if (this.thumbnailUrl) {
        return this.thumbnailUrl;
      } else {
        return this.newThumbnailUrl;
      }
    },
    shouldShowRemoveButtonAndReorderHandle() {
      return this.util.isTargetedForStory();
    },
  },
  created() {
    this.util = new VideoUploaderUtil(this.$extension);
  },
  mounted() {
    // If component receives the file
    if (this.file) {
      // Generate and upload thumbnail, then upload video
      this.isGeneratingThumbnail = true;
      const {thumbnailGenerator} = this.$refs;
      thumbnailGenerator.startThumbnailGeneration();
    }
  },
  methods: {
    //**********************************************************************************************
    // Thumbnail related methods
    //**********************************************************************************************

    onThumbnailGenerationCompleted() {
      const {thumbnailGenerator} = this.$refs;
      const thumbnailKey = this.$s3.thumbnailKey(this.originalFilename, this.uuid);

      this.$s3.uploadFile(thumbnailKey, thumbnailGenerator.thumbnailBlob)
        .promise()
        .then(this.successfullyUploadedThumbnail)
        .catch(this.thumbnailUnavailable);
    },
    successfullyUploadedThumbnail(s3Result) {
      console.log('thumbnail is successfully uploaded!', s3Result);
      this.newThumbnailUrl = s3Result.Location;
      this.startUploadingFile();
    },
    thumbnailUnavailable(err) {
      console.error('thumbnail is not available: ', err);
      this.startUploadingFile();
    },

    //**********************************************************************************************
    // Video related methods
    //**********************************************************************************************

    startUploadingFile() {
      this.isGeneratingThumbnail = false;

      this.startedUploadingAt = new Date();
      const videoKey = this.$s3.videoKey(this.originalFilename, this.uuid);
      const upload = this.$s3.uploadFile(videoKey, this.file, this.updateProgressStatus);
      this.managedUpload = upload;
      upload.promise()
        .then(this.commitUploadedVideoRecord)
        .catch(this.handleUploadError);
    },
    updateProgressStatus(progress, uploaded, total) {
      this.isUploadInProgress = true;
      this.totalFileSize = total;
      this.uploadedBytes = uploaded;
      this.progress = progress;
    },
    commitUploadedVideoRecord(result) {
      this.isUploadInProgress = false;
      this.managedUpload = null;
      console.log('successfully uploaded file');
      console.log(result);

      const video = {
        uuid: this.uuid,
        title: this.title,
        originalFilename: this.originalFilename,
        s3Key: result.Key,
        publicUrl: result.Location,
        thumbnailUrl: this.newThumbnailUrl,
        fileSize: this.totalFileSize,
      };

      if (this.util.isTargetedForStory()) {
        console.log('targeted for story');
        this.$store.dispatch('createVideoEntryAndSaveVideoReferences', video);
      } else if (this.util.isTargetedForVideo()) {
        console.log('targeted for video');
        this.$store.dispatch('saveVideoEntry', video);
      }
    },
    handleUploadError(err) {
      this.isUploadInProgress = false;
      this.managedUpload = null;

      console.log('[ERROR] error uploading objects Reason -> ', err);
      console.log(err);
      console.log(err.name);
      console.log(err.message);

      if (err.name === NetworkingError) {
        // Show networking error dialog
        this.showNetworkErrorDialog();
        console.log('networking error detected');
      } else if (err.name === RequestAbortedError) {
        // Doesn't show dialog here, because cancellation is not an error
        console.log('request aborted error detected');
      } else {
        // Show unknown error dialog
        this.showUnknownErrorDialog(err.name, err.message);
      }

      console.log('removing this video from the list, id:', this.uuid);
      this.$store.commit('removeVideo', this.uuid);
    },
    showNetworkErrorDialog() {
      this.$extension.dialogs.openAlert({
        title: 'Network Error',
        message: 'It seems that you are offline or your internet connection is lost. Please try again after regaining connection.',
        confirmLabel: 'OK',
      });
    },
    showUnknownErrorDialog(name, message) {
      this.$extension.dialogs.openAlert({
        title: 'Unknown Error',
        message: `An unknown error occurred (name: ${name}, message: ${message}). Please let Sosolimited know if this issues persists. Thank you.`,
        confirmLabel: 'OK',
      });
    },
    onRemoveOrCancelClicked() {
      console.log('onRemoveOrCancelClicked', this.uuid);
      if (this.isUploadInProgress) {
        this.confirmAndCancelUploading();
      } else {
        this.confirmAndDeleteVideo();
      }
    },
    confirmAndCancelUploading() {
      this.$extension.dialogs.openConfirm({
        title: 'Canceling the Upload',
        message: `Are you sure you want to cancel uploading ${this.title}?`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
      }).then((result) => {
        // When the user chose Yes, and the upload is still ongoing
        if (result && this.isUploadInProgress) {
          this.managedUpload.abort();
        }
      });
    },
    confirmAndDeleteVideo() {
      this.$extension.dialogs.openConfirm({
        title: 'Deleting Video',
        message: `Are you sure you want to delete ${this.title}?`,
        confirmLabel: 'Yes',
        cancelLabel: 'No',
      }).then((result) => {
        if (result) {
          this.$store.commit('removeVideo', this.uuid);
          this.$store.dispatch('saveVideoReferencesToContentful');
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
  .video-row {
    position: relative;
    display: block;
    padding: 10px 16px;
    margin-bottom: 10px;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, .125);
    max-width: 540px;
  }

  .container {
    display: flex;
    align-items: center;

    .title-and-size {
      flex: 1;

      display: flex;
      flex-direction: column;

      .title, .status-message {
        flex: 1;

        width: 340px;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      p {
        margin: 0;
      }

      .status-message {
        font-size: 11px;
        line-height: 11px;
        padding-bottom: 2px;
        color: #8091a5;
      }
    }

    p {
      flex: 1;
      padding-top: 3px;
    }

    span.icon {
      flex: initial;
      margin: 0 4px;
      display: inline-block;
      width: 24px;
      height: 24px;

      svg {
        fill: #a9b9c0;
      }

      &.delete {
        svg {
          cursor: pointer;
        }

        svg:hover {
          fill: #f05650;
        }
      }

      &.handle {
        svg:hover {
          fill: #8091a5;
        }
      }
    }
  }

  .handle {
    cursor: move;
  }
</style>
